/**
 * Export Utility for Attendance Reports
 * Provides Excel and PDF export functionality
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

class ExportUtility {
    /**
     * Export attendance data to Excel
     * @param {Object} config - Export configuration
     */
    static async exportToExcel(config = {}) {
        const {
            attendanceData = [],
            filters = {},
            summary = {},
            dateRange = [],
            holidays = [],
            statuses = []
        } = config;

        if (attendanceData.length === 0) {
            alert('No data to export');
            return;
        }

        try {
            // Create workbook
            const wb = XLSX.utils.book_new();

            // Main data sheet
            const mainData = this.prepareExcelData(attendanceData, dateRange, holidays);
            const ws = XLSX.utils.aoa_to_sheet(mainData);

            // Set column widths
            const colWidths = this.calculateColumnWidths(mainData);
            ws['!cols'] = colWidths;

            // Add summary sheet
            const summaryData = this.prepareSummarySheet(summary, filters);
            const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);

            // Add status legend sheet
            const legendData = this.prepareLegendSheet(statuses);
            const wsLegend = XLSX.utils.aoa_to_sheet(legendData);

            // Add sheets to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Attendance Data');
            XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
            XLSX.utils.book_append_sheet(wb, wsLegend, 'Legend');

            // Generate filename
            const fileName = `Attendance_Report_${filters.from_date}_to_${filters.to_date}_${new Date().getTime()}.xlsx`;

            // Save file
            XLSX.writeFile(wb, fileName);

            return fileName;
        } catch (error) {
            console.error('Excel export error:', error);
            alert(`Error exporting to Excel: ${error.message}`);
            throw error;
        }
    }

    /**
     * Prepare main data for Excel sheet
     */
    static prepareExcelData(attendanceData, dateRange, holidays) {
        const data = [];

        // Header row
        const header = [
            'S.No', 'Unit', 'Employee ID', 'Employee Name', 'Department',
            'Designation', 'ED', 'WD', 'FH', 'LOff', 'LOP'
        ];

        // Add date headers
        dateRange.forEach(date => {
            const day = new Date(date);
            header.push(`${day.getDate()} ${day.toLocaleDateString('en-US', { weekday: 'short' })}`);
        });

        data.push(header);

        // Add employee rows
        attendanceData.forEach((emp, index) => {
            const row = [
                index + 1,
                emp.unit_name || '',
                emp.emp_code || '',
                emp.emp_name || '',
                emp.dept_name || '',
                emp.desig_name || '',
                emp.ed || 0,
                emp.wd || 0,
                emp.fh || 0,
                emp.loff || 0,
                emp.lop || 0
            ];

            // Add daily status
            dateRange.forEach(date => {
                const dayData = emp.daily_data?.[date] || {};
                let status = '';

                if (dayData.is_holiday) {
                    status = 'H';
                } else if (dayData.is_layoff) {
                    status = 'LO';
                } else if (dayData.is_weeklyoff) {
                    status = 'WO';
                } else if (dayData.status_code) {
                    status = dayData.status_code;
                } else {
                    status = '-';
                }

                row.push(status);
            });

            data.push(row);
        });

        // Add summary row
        const summaryRow = ['', '', '', '', '', 'TOTAL:',
            this.getTotal(attendanceData, 'ed'),
            this.getTotal(attendanceData, 'wd'),
            this.getTotal(attendanceData, 'fh'),
            this.getTotal(attendanceData, 'loff'),
            this.getTotal(attendanceData, 'lop')
        ];

        // Fill remaining cells for summary row
        for (let i = 0; i < dateRange.length; i++) {
            summaryRow.push('');
        }

        data.push(summaryRow);

        return data;
    }

    /**
     * Prepare summary sheet data
     */
    static prepareSummarySheet(summary, filters) {
        const data = [];

        // Title
        data.push(['ATTENDANCE SUMMARY REPORT']);
        data.push([]);

        // Date range
        data.push(['Report Period:', `${filters.from_date} to ${filters.to_date}`]);
        data.push(['Generated Date:', new Date().toLocaleDateString()]);
        data.push(['Generated Time:', new Date().toLocaleTimeString()]);
        data.push([]);

        // Summary statistics
        data.push(['SUMMARY STATISTICS']);
        data.push(['Total Employees', summary.total_employees || 0]);
        data.push(['Total ED', summary.total_ed || 0]);
        data.push(['Total WD', summary.total_wd || 0]);
        data.push(['Total FH', summary.total_fh || 0]);
        data.push(['Total LOff', summary.total_loff || 0]);
        data.push(['Total LOP', summary.total_lop || 0]);
        data.push([]);

        // Daily totals
        if (summary.daily_totals && summary.daily_totals.length > 0) {
            data.push(['DAILY ATTENDANCE TOTALS']);
            data.push(['Date', 'Present', 'Absent', 'Leave', 'Holiday']);

            summary.daily_totals.forEach(daily => {
                data.push([
                    daily.date,
                    daily.present_count || 0,
                    daily.absent_count || 0,
                    daily.leave_count || 0,
                    daily.holiday_count || 0
                ]);
            });
        }

        return data;
    }

    /**
     * Prepare legend sheet data
     */
    static prepareLegendSheet(statuses) {
        const data = [];

        data.push(['ATTENDANCE STATUS LEGEND']);
        data.push([]);

        // Default statuses
        const defaultStatuses = [
            { status_code: 'P', status_name: 'Present' },
            { status_code: 'A', status_name: 'Absent' },
            { status_code: 'H', status_name: 'Holiday' },
            { status_code: 'WO', status_name: 'Weekly Off' },
            { status_code: 'LO', status_name: 'Layoff' }
        ];

        const allStatuses = [...defaultStatuses, ...statuses];

        data.push(['Code', 'Description']);
        allStatuses.forEach(status => {
            if (status.status_code && status.status_name) {
                data.push([status.status_code, status.status_name]);
            }
        });

        return data;
    }

    /**
     * Calculate column widths for Excel
     */
    static calculateColumnWidths(data) {
        const colWidths = [];

        if (data.length === 0) return colWidths;

        const numCols = data[0].length;

        for (let col = 0; col < numCols; col++) {
            let maxLength = 0;

            for (let row = 0; row < data.length; row++) {
                if (data[row] && data[row][col]) {
                    const cellLength = String(data[row][col]).length;
                    maxLength = Math.max(maxLength, cellLength);
                }
            }

            // Set width with some padding
            colWidths.push({ wch: Math.min(Math.max(maxLength + 2, 10), 30) });
        }

        return colWidths;
    }

    /**
     * Get total for a specific field
     */
    static getTotal(data, field) {
        return data.reduce((sum, emp) => sum + (parseFloat(emp[field]) || 0), 0);
    }

    /**
     * Export attendance data to PDF
     * @param {Object} config - Export configuration
     */
    static async exportToPDF(config = {}) {
        const {
            attendanceData = [],
            filters = {},
            summary = {},
            dateRange = [],
            holidays = []
        } = config;

        if (attendanceData.length === 0) {
            alert('No data to export');
            return;
        }

        try {
            // Create PDF document (landscape orientation)
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a3' // Use A3 for better width handling
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - (margin * 2);

            let currentY = margin;

            // Add header
            currentY = this.addPDFHeader(doc, pageWidth, margin, currentY, filters);

            // Add summary section
            currentY = this.addPDFSummary(doc, pageWidth, margin, currentY, summary);

            // Add legend
            currentY = this.addPDFLegend(doc, pageWidth, margin, currentY);

            // Add table
            this.addPDFTable(doc, margin, currentY, contentWidth, attendanceData, dateRange, holidays, summary);

            // Add footer
            this.addPDFFooter(doc, pageWidth, pageHeight, margin);

            // Save PDF
            const fileName = `Attendance_Report_${filters.from_date}_to_${filters.to_date}_${new Date().getTime()}.pdf`;
            doc.save(fileName);

            return fileName;
        } catch (error) {
            console.error('PDF export error:', error);
            alert(`Error exporting to PDF: ${error.message}`);
            throw error;
        }
    }

    /**
     * Add PDF header
     */
    static addPDFHeader(doc, pageWidth, margin, currentY, filters) {
        // Company header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('COMPANY NAME', pageWidth / 2, currentY, { align: 'center' });

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text('Attendance Management System', pageWidth / 2, currentY + 6, { align: 'center' });

        currentY += 15;

        // Report title
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('ATTENDANCE REPORT', pageWidth / 2, currentY, { align: 'center' });

        currentY += 8;

        // Date range
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${filters.from_date} to ${filters.to_date}`, pageWidth / 2, currentY, { align: 'center' });

        currentY += 6;

        // Generated date
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, pageWidth / 2, currentY, { align: 'center' });

        currentY += 10;

        return currentY;
    }

    /**
     * Add PDF summary section
     */
    static addPDFSummary(doc, pageWidth, margin, currentY, summary) {
        const summaryItems = [
            { label: 'Total Employees', value: summary.total_employees || 0 },
            { label: 'Total ED', value: summary.total_ed || 0 },
            { label: 'Total WD', value: summary.total_wd || 0 },
            { label: 'Total FH', value: summary.total_fh || 0 },
            { label: 'Total LOff', value: summary.total_loff || 0 },
            { label: 'Total LOP', value: summary.total_lop || 0 }
        ];

        const boxWidth = (pageWidth - (margin * 2) - 25) / summaryItems.length;

        doc.setFontSize(10);

        summaryItems.forEach((item, index) => {
            const x = margin + (boxWidth * index) + (index * 5);

            // Draw box
            doc.setDrawColor(200, 200, 200);
            doc.rect(x, currentY, boxWidth, 15);

            // Add value
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text(item.value.toString(), x + (boxWidth / 2), currentY + 7, { align: 'center' });

            // Add label
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.text(item.label, x + (boxWidth / 2), currentY + 13, { align: 'center' });
        });

        currentY += 20;

        return currentY;
    }

    /**
     * Add PDF legend
     */
    static addPDFLegend(doc, pageWidth, margin, currentY) {
        const legendItems = [
            { code: 'P', label: 'Present', color: [209, 231, 221] },
            { code: 'A', label: 'Absent', color: [248, 215, 218] },
            { code: 'CL', label: 'Casual Leave', color: [207, 244, 252] },
            { code: 'EL', label: 'Earned Leave', color: [207, 226, 255] },
            { code: 'PA', label: 'Half Day', color: [255, 243, 205] },
            { code: 'H', label: 'Holiday', color: [226, 217, 243] },
            { code: 'LO', label: 'Layoff', color: [211, 211, 212] },
            { code: 'WO', label: 'Weekly Off', color: [226, 227, 229] }
        ];

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Legend:', margin, currentY);

        doc.setFont('helvetica', 'normal');

        let currentX = margin + 20;

        legendItems.forEach((item, index) => {
            if (currentX > pageWidth - 100) {
                currentX = margin + 20;
                currentY += 6;
            }

            // Draw color box
            doc.setFillColor(...item.color);
            doc.rect(currentX, currentY - 3, 4, 4, 'F');

            // Add code and label
            doc.text(`${item.code}: ${item.label}`, currentX + 6, currentY);

            currentX += 45;
        });

        currentY += 10;
        return currentY;
    }

    /**
     * Add PDF table
     */
    static addPDFTable(doc, margin, currentY, contentWidth, attendanceData, dateRange, holidays, summary) {
        // Prepare table headers
        const headers = [
            'S.No',
            'Unit',
            'Employee',
            'Dept',
            'Designation',
            'ED',
            'WD',
            'FH',
            'LOff',
            'LOP'
        ];

        // Add date headers
        dateRange.forEach(date => {
            const day = new Date(date);
            headers.push(`${day.getDate()}\n${day.toLocaleDateString('en-US', { weekday: 'short' })}`);
        });

        // Prepare table body
        const body = attendanceData.map((emp, index) => {
            const row = [
                (index + 1).toString(),
                emp.unit_name || '',
                `${emp.emp_name}\n(${emp.emp_code})`,
                emp.dept_name || '',
                emp.desig_name || '',
                emp.ed?.toString() || '0',
                emp.wd?.toString() || '0',
                emp.fh?.toString() || '0',
                emp.loff?.toString() || '0',
                emp.lop?.toString() || '0'
            ];

            // Add daily status
            dateRange.forEach(date => {
                const dayData = emp.daily_data?.[date] || {};
                let status = '';

                if (dayData.is_holiday) {
                    status = 'H';
                } else if (dayData.is_layoff) {
                    status = 'LO';
                } else if (dayData.is_weeklyoff) {
                    status = 'WO';
                } else if (dayData.status_code) {
                    status = dayData.status_code;
                } else {
                    status = '-';
                }

                row.push(status);
            });

            return row;
        });

        // Add summary row
        const summaryRow = [
            '', '', '', '', '',
            'TOTAL:',
            summary.total_ed?.toString() || '0',
            summary.total_wd?.toString() || '0',
            summary.total_fh?.toString() || '0',
            summary.total_loff?.toString() || '0',
            summary.total_lop?.toString() || '0'
        ];

        // Fill remaining cells with empty strings
        for (let i = 0; i < dateRange.length; i++) {
            summaryRow.push('');
        }

        body.push(summaryRow);

        // Calculate column widths
        const colWidths = this.calculatePDFColumnWidths(headers, body, contentWidth);

        // Generate table
        doc.autoTable({
            startY: currentY,
            head: [headers],
            body: body,
            margin: { left: margin, right: margin },
            tableWidth: contentWidth,
            columnStyles: {
                0: { cellWidth: colWidths[0], halign: 'center' },
                5: { cellWidth: colWidths[5], halign: 'center' },
                6: { cellWidth: colWidths[6], halign: 'center' },
                7: { cellWidth: colWidths[7], halign: 'center' },
                8: { cellWidth: colWidths[8], halign: 'center' },
                9: { cellWidth: colWidths[9], halign: 'center' }
            },
            didParseCell: function (data) {
                // Style summary row
                if (data.row.index === body.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }

                // Style date cells (narrow columns)
                if (data.column.index >= 10) {
                    data.cell.styles.cellWidth = colWidths[data.column.index];
                    data.cell.styles.halign = 'center';
                    data.cell.styles.fontSize = 8;

                    // Color code statuses
                    const cellValue = data.cell.text[0];
                    if (cellValue === 'P') {
                        data.cell.styles.fillColor = [209, 231, 221];
                    } else if (cellValue === 'A') {
                        data.cell.styles.fillColor = [248, 215, 218];
                    } else if (cellValue === 'H') {
                        data.cell.styles.fillColor = [226, 217, 243];
                    } else if (cellValue === 'WO') {
                        data.cell.styles.fillColor = [226, 227, 229];
                    } else if (cellValue === 'LO') {
                        data.cell.styles.fillColor = [211, 211, 212];
                    }
                }
            },
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                lineWidth: 0.1
            },
            headStyles: {
                fillColor: [52, 58, 64],
                textColor: 255,
                fontSize: 9,
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [248, 249, 250]
            }
        });
    }

    /**
     * Calculate PDF column widths
     */
    static calculatePDFColumnWidths(headers, body, totalWidth) {
        const colWidths = [];
        const numCols = headers.length;

        // Fixed widths for first columns
        const fixedWidths = [15, 30, 50, 30, 30, 15, 15, 15, 15, 15];

        // Calculate remaining width for date columns
        const fixedWidthTotal = fixedWidths.reduce((sum, width) => sum + width, 0);
        const remainingWidth = totalWidth - fixedWidthTotal;
        const dateColsCount = numCols - fixedWidths.length;
        const dateColWidth = Math.max(8, remainingWidth / dateColsCount);

        // Combine widths
        for (let i = 0; i < numCols; i++) {
            if (i < fixedWidths.length) {
                colWidths[i] = fixedWidths[i];
            } else {
                colWidths[i] = dateColWidth;
            }
        }

        return colWidths;
    }

    /**
     * Add PDF footer
     */
    static addPDFFooter(doc, pageWidth, pageHeight, margin) {
        const pageCount = doc.internal.getNumberOfPages();

        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            // Footer line
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

            // Footer text
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
            );

            doc.text(
                'Confidential - Attendance Management System',
                margin,
                pageHeight - 10
            );

            doc.text(
                `Generated: ${new Date().toLocaleString()}`,
                pageWidth - margin,
                pageHeight - 10,
                { align: 'right' }
            );
        }
    }

    /**
     * Export specific employee details
     */
    static async exportEmployeeDetails(employee, dateRange, filters, format = 'pdf') {
        if (!employee) {
            alert('No employee data to export');
            return;
        }

        if (format === 'pdf') {
            return this.exportEmployeeToPDF(employee, dateRange, filters);
        } else {
            return this.exportEmployeeToExcel(employee, dateRange, filters);
        }
    }

    /**
     * Export employee details to PDF
     */
    static async exportEmployeeToPDF(employee, dateRange, filters) {
        const doc = new jsPDF();

        // Header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Employee Attendance Details', 105, 20, { align: 'center' });

        // Employee info
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Name: ${employee.emp_name}`, 20, 40);
        doc.text(`Employee ID: ${employee.emp_code}`, 20, 50);
        doc.text(`Department: ${employee.dept_name}`, 20, 60);
        doc.text(`Designation: ${employee.desig_name}`, 20, 70);
        doc.text(`Period: ${filters.from_date} to ${filters.to_date}`, 20, 80);

        // Summary
        doc.setFont('helvetica', 'bold');
        doc.text('Attendance Summary:', 20, 95);

        doc.setFont('helvetica', 'normal');
        const summaryY = 105;
        doc.text(`ED: ${employee.ed || 0}`, 30, summaryY);
        doc.text(`WD: ${employee.wd || 0}`, 70, summaryY);
        doc.text(`FH: ${employee.fh || 0}`, 110, summaryY);
        doc.text(`LOP: ${employee.lop || 0}`, 150, summaryY);

        // Daily attendance table
        const tableData = [];

        dateRange.forEach(date => {
            const dayData = employee.daily_data?.[date] || {};
            const dateObj = new Date(date);

            tableData.push([
                date,
                dateObj.toLocaleDateString('en-US', { weekday: 'long' }),
                this.getStatusText(dayData),
                dayData.in_time || '',
                dayData.out_time || '',
                dayData.work_hours || ''
            ]);
        });

        doc.autoTable({
            startY: 120,
            head: [['Date', 'Day', 'Status', 'In Time', 'Out Time', 'Hours']],
            body: tableData,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [52, 58, 64] }
        });

        // Footer
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(10);
        doc.text(
            `Generated on ${new Date().toLocaleDateString()}`,
            105,
            pageHeight - 10,
            { align: 'center' }
        );

        const fileName = `Attendance_${employee.emp_code}_${filters.from_date}_to_${filters.to_date}.pdf`;
        doc.save(fileName);

        return fileName;
    }

    /**
     * Get status text for display
     */
    static getStatusText(dayData) {
        if (dayData.is_holiday) {
            return `Holiday: ${dayData.holiday_name || ''}`;
        } else if (dayData.is_layoff) {
            return `Layoff: ${dayData.layoff_reason || ''}`;
        } else if (dayData.is_weeklyoff) {
            return 'Weekly Off';
        } else if (dayData.status_code) {
            return `${dayData.status_code}: ${dayData.status_name || ''}`;
        }
        return '-';
    }
}

export default ExportUtility;